import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { MIN_PASSWORD_LENGTH } from 'projects/admin/src/app/shared/constants';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { LANGUAGES, SALUTATIONS } from 'projects/core/src/lib/constants';
import { RoleModel } from 'projects/core/src/lib/models/role.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { forkJoin } from 'rxjs';

const SEPARATOR = ',';

@Component({
  selector: 'app-role-basedata',
  templateUrl: './role-basedata.component.html',
  styleUrls: ['./role-basedata.component.scss']
})
export class RoleBasedataComponent extends BaseForm implements OnInit, OnChanges {

  @Input() role: RoleModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public loading = false;

  public form = this.fb.group({
    name: new UntypedFormControl(''),
    title_de: new UntypedFormControl('', [Validators.required]),
    title_en: new UntypedFormControl('', [Validators.required]),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ui.setFocus();
    this.form.get('name').disable();
  }

  ngOnChanges(): void {
    this.items = [];
    if (this.role !== null) {
      if (this.auth.hasRight('admin.role.delete')) {
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.role.deletable),
          command: () => {
            this.onDelete();
          }
        });
      }
    }
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        id: this.role.id,
        name: this.form.get('name').value,
        title_de: this.form.get('title_de').value,
        title_en: this.form.get('title_en').value,
      };
      this.api.saveModel('roles', this.role.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.role.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  onDelete(): void {
    if (this.auth.hasRight('admin.role.delete')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.role.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('roles', this.role.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.role.name }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/roles']);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  protected startEdit(): void {
    if (this.role !== null) {
      this.form.patchValue({
        name: this.role.name,
        title_de: this.role.title['DE'] || null,
        title_en: this.role.title['EN'] || null,
      });
    }
    this.setEdit.emit('basedata');
    super.startEdit();
    this.ui.setFormFocus();
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
  }

}
